/**
 * Authenticated fetch wrapper
 * Uses httpOnly cookies for authentication instead of localStorage tokens
 * This is more secure as cookies are not accessible via JavaScript (XSS protection)
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Make an authenticated fetch request using httpOnly cookies
 * @param {string} endpoint - API endpoint (e.g., '/api/users')
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Response>} - Fetch response
 */
export async function authFetch(endpoint, options = {}) {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  
  const defaultOptions = {
    credentials: 'include', // Include cookies in request
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  // Merge options
  const fetchOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (options.body instanceof FormData) {
    delete fetchOptions.headers['Content-Type'];
  }

  return fetch(url, fetchOptions);
}

/**
 * GET request with authentication
 */
export async function authGet(endpoint, options = {}) {
  return authFetch(endpoint, { ...options, method: 'GET' });
}

/**
 * POST request with authentication
 */
export async function authPost(endpoint, body, options = {}) {
  return authFetch(endpoint, {
    ...options,
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body),
  });
}

/**
 * PUT request with authentication
 */
export async function authPut(endpoint, body, options = {}) {
  return authFetch(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * DELETE request with authentication
 */
export async function authDelete(endpoint, options = {}) {
  return authFetch(endpoint, { ...options, method: 'DELETE' });
}

export default authFetch;
